// The running timer, wherever you are (PRD 64 §4).
//
// IN THE SHELL, NOT ON A PAGE. §4: the timer lives in the app shell "so that
// starting one does not mean navigating away from the work". It follows you
// across every screen, and it is the only thing on screen that says a clock is
// running — a timer you cannot see from the page you are on is a timer that
// runs all night.
//
// IT COUNTS FROM THE SERVER'S START TIME, not from a tick it accumulates. A
// laptop that slept for an hour would otherwise report the seconds it was
// awake for, which is the failure `timer_started_at` being a column exists to
// prevent in the first place.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { XStack, YStack } from 'tamagui';
import { GBButton, GBCard, GBText } from '@greenbackapp/ui';
import { practiceTimeService } from '../../services/practiceTimeService';
import {
  elapsedSeconds,
  formatElapsed,
  getRunningTimer,
  setRunningTimer,
  subscribeRunningTimer,
} from '../../services/runningTimerStore';
import { TimeEntrySummary } from '../../types/practiceTimeApi';

interface RunningTimerBarProps {
  firmId: string | null;
}

export const RunningTimerBar: React.FC<RunningTimerBarProps> = ({ firmId }) => {
  const navigate = useNavigate();
  const [entry, setEntry] = useState<TimeEntrySummary | null>(getRunningTimer());
  const [seconds, setSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => subscribeRunningTimer(setEntry), []);

  // THE SERVER IS ASKED, once, on load. A timer started on another machine —
  // or before this browser was opened — is still running, and a shell that
  // only knew about timers it started itself would show nothing.
  useEffect(() => {
    if (!firmId) return;
    practiceTimeService
      .runningTimer(firmId)
      .then(setRunningTimer)
      // Silent: a shell element that shouts about a failed poll is worse than
      // one that simply has nothing to show.
      .catch(() => undefined);
  }, [firmId]);

  useEffect(() => {
    if (!entry) return undefined;
    const tick = () => setSeconds(elapsedSeconds(entry));
    tick();
    const handle = window.setInterval(tick, 1000);
    return () => window.clearInterval(handle);
  }, [entry]);

  const stop = useCallback(async () => {
    if (!firmId || busy) return;
    setBusy(true);
    try {
      await practiceTimeService.stopTimer(firmId);
      setRunningTimer(null);
    } catch {
      // Leave it running in the UI: saying it stopped when it did not is the
      // one wrong answer here.
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [firmId, busy]);

  if (!entry) return null;

  const where = entry.work_item?.title ?? entry.client?.name ?? 'Untitled';

  return (
    <GBCard padding={12} marginBottom={12}>
      <XStack gap={12} alignItems="center" justifyContent="space-between" flexWrap="wrap">
        <YStack gap={2} flexGrow={1} minWidth={160}>
          <GBText variant="bodySmall" bold>{`Timer running · ${formatElapsed(seconds)}`}</GBText>
          <GBText variant="bodySmall" color="secondary">
            {entry.client?.name ? `${where} · ${entry.client.name}` : where}
          </GBText>
        </YStack>
        <XStack gap={8} flexWrap="wrap">
          {entry.work_item && (
            <GBButton
              size="sm"
              variant="secondary"
              onPress={() => navigate(`/firm/work/${entry.work_item?.id}`)}
            >
              Go to the work
            </GBButton>
          )}
          <GBButton size="sm" variant="primary" disabled={busy} onPress={() => void stop()}>
            {busy ? 'Stopping…' : 'Stop'}
          </GBButton>
        </XStack>
      </XStack>
    </GBCard>
  );
};

export default RunningTimerBar;
